/**
 * <TutorLiveMosaic> — la mosaïque du live du masteur : tout le groupe, sur tout l'écran (organism).
 *
 * L'ancienne grille (`TutorGridLayoutM`, ouverte par `LayoutToggleM` dans la barre du bas), reprise au
 * round 3 (Wajih : « un peu comme avant ») : un mode de tout l'écran, pas un onglet du centre. La règle
 * de colonnes d'aujourd'hui (1 · 2 · 2 · 2 · 3…), la dernière rangée centrée, chaque case à sa part de
 * la hauteur. Chaque élève garde ce que sa vignette dit : sa puce la plus urgente, le liseré vert quand
 * il parle, et sa bascule Dialogue · Concentré dessous. Toucher sa vidéo le regarde (`onSelect`) : la barre
 * referme la mosaïque, c'est l'écran qui le décide.
 *
 * Round 4 (Wajih : « l'activité en cours de chaque élève doit être visible ») : chaque case est la carte de
 * l'élève de la pellicule (`<TutorStudentTile>`, qui remplit sa case) — sa vidéo, sa bascule, l'activité qu'il
 * a devant lui, qui s'ouvre au centre, et ce qui attend le masteur chez lui.
 *
 * Le masteur s'y voit (Wajih, weekly du 03/11 : « comme dans l'ancienne mosaïque ») : sa vignette (`me`) est la
 * dernière case, « Toi ». La mosaïque recouvre la pellicule, qui la portait ; sans elle, il ne se voyait plus.
 *
 * Contrôlée : les élèves et leurs modes arrivent en props, chaque geste repart.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UILiveParticipant, type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { tutorMeParticipant } from "../../molecules/TutorStudentVideo"
import { TutorStudentTile } from "../../molecules/TutorStudentTile"
import { VideoTile } from "../../molecules/VideoTile"

export type TutorLiveMosaicProps = {
    students: UITutorLiveStudent[]
    /** Le masteur : sa vignette, la dernière case. Absent : les élèves seuls. */
    me?: UILiveParticipant
    onSelect: (studentId: string) => void
    onChangeMode: (studentId: string, mode: UISoundMode) => void
    /** Ouvrir au centre l'activité qu'un élève a devant lui. Absent : la barre se lit, sans geste. */
    onOpenActivity?: (studentId: string, activityId: string) => void
    /** L'élève dont la bascule part. */
    modePendingId?: string
    /** La vraie piste d'un élève. Absente : sa photo, ou son visage. */
    renderVideo?: (participantId: string) => React.ReactNode
    testID?: string
}

/** Les colonnes de la grille d'aujourd'hui, selon le nombre d'élèves (`tutorGridLayout`). */
const GRID_COLUMNS = [1, 1, 2, 2, 2, 3, 3, 3, 3, 3, 4]

export function TutorLiveMosaic(props: TutorLiveMosaicProps) {
    const { students, me, onSelect, onChangeMode, onOpenActivity, modePendingId, renderVideo, testID } = props
    const cells = students.length + (me != null ? 1 : 0)
    const columns = GRID_COLUMNS[Math.min(cells, GRID_COLUMNS.length - 1)] ?? 4
    const rows = Math.max(1, Math.ceil(cells / columns))
    const meVideo = me != null ? renderVideo?.(me.id) : undefined
    return (
        <View style={styles.grid} testID={testID}>
            {students.map((student) => (
                <View key={student.id} style={styles.cell(columns, rows)}>
                    <TutorStudentTile
                        student={student}
                        onSelect={() => onSelect(student.id)}
                        onChangeMode={(mode) => onChangeMode(student.id, mode)}
                        modePending={student.id === modePendingId}
                        video={renderVideo?.(student.id)}
                        onOpenActivity={
                            onOpenActivity != null ? (activityId) => onOpenActivity(student.id, activityId) : undefined
                        }
                    />
                </View>
            ))}
            {me != null && (
                <View style={styles.cell(columns, rows)}>
                    <VideoTile participant={tutorMeParticipant(me, meVideo)} size="tile" me video={meVideo} />
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    grid: {
        flex: 1,
        minHeight: 0,
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        alignContent: "center",
        gap: theme.spacing["3"],
    },
    /** Chaque case prend sa part de la largeur ET de la hauteur : la mosaïque tient toujours dans l'écran. */
    cell: (columns: number, rows: number) => ({
        width: `${Math.floor(100 / columns) - 2}%` as const,
        height: `${Math.floor(100 / rows) - 3}%` as const,
    }),
}))
